import React, { useState, useMemo } from 'react';
import { Tag } from 'antd';
import { makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import Copy from 'src/component/Copy';
import InnerHeader from 'src/component/InnerHeader';
import NoxTable from 'src/component/Table';
import { SearchBy } from 'src/component/Table/enums/searchby.enum';
import { formatDate } from 'src/helpers/millisecondsToFormattedDate';
import Loader from 'src/component/Loader/Loader';
import { useGetBoxes } from 'src/reactQuery/box/useGetBoxes';
import { useDeleteBox } from 'src/reactQuery/box/useDeleteBox';
import {
  BoxResponseData,
  BoxTableData,
  isBoxRead,
  isSurveyResultBoxType
} from 'src/reactQuery/box/interfaces/box.interface';
import { isCurrentProjectCitizen } from 'src/util/projectCheckers';

const useStyles = makeStyles({
  garbage: {
    cursor: 'pointer',
    left: '8px',
    position: 'absolute',
    top: '0px'
  },
  passwordWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  },
  select: {
    marginTop: '16px'
  },
  unreadRow: {
    '& > td': {
      fontWeight: 600
    }
  }
});

/** `createdAt` is typed as a Date but arrives as an ISO string; unparseable values sort last. */
const toTimestamp = (value: Date): number => {
  const time = new Date(value).getTime();

  return Number.isNaN(time) ? 0 : time;
};

const Boxes = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[] | undefined>([]);
  const { data, isLoading, refetch } = useGetBoxes();
  const { mutate } = useDeleteBox({ onSuccess: refetch });
  const classes = useStyles();
  const history = useHistory();

  const filteredData: BoxTableData[] = useMemo(() => {
    if (!data) {
      return [];
    }
    const filteredNotifications: BoxTableData[] = data?.data.map(
      // @ts-ignore
      (box: BoxResponseData) => {
        const { _id, type, user, createdAt, updatedAt } = box;
        return {
          _id,
          createdAt,
          isRead: isBoxRead(box),
          key: _id,
          type,
          updatedAt,
          user,
          userName: isCurrentProjectCitizen ? box.fullName || undefined : undefined
        };
      }
    );

    if (!isCurrentProjectCitizen) {
      return filteredNotifications;
    }

    // The API returns boxes in insertion order, so newly submitted survey results would
    // land on the last page. Show them first instead.
    return filteredNotifications.sort(
      (a, b) => toTimestamp(b.createdAt) - toTimestamp(a.createdAt)
    );
  }, [data?.data]);

  const columns = [
    {
      dataIndex: '_id',
      key: 'id',
      render: (_id: string) => {
        return <Copy text={_id} />;
      },
      sorter: (a: BoxTableData, b: BoxTableData) => a._id.localeCompare(b._id),
      title: 'ID'
    },
    {
      dataIndex: 'user',
      key: 'user',
      render: (user: string, record: BoxTableData) => {
        return <Copy text={record.userName || user} />;
      },
      sorter: (a: BoxTableData, b: BoxTableData) =>
        (a.userName || a.user).localeCompare(b.userName || b.user),
      title: 'User'
    },
    {
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => {
        return <Copy text={type} />;
      },
      sorter: (a: BoxTableData, b: BoxTableData) => a.type.toString().localeCompare(b.type),
      title: 'Type'
    },
    // Survey submissions land here unread; opening the record's page marks them read.
    ...(isCurrentProjectCitizen
      ? [
          {
            dataIndex: 'isRead',
            key: 'status',
            render: (isRead: boolean, record: BoxTableData) => {
              if (!isSurveyResultBoxType(record.type)) {
                return '-';
              }

              return isRead ? 'Read' : 'Unread';
            },
            sorter: (a: BoxTableData, b: BoxTableData) => Number(a.isRead) - Number(b.isRead),
            title: 'Status'
          }
        ]
      : []),
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: Date) => {
        return <Copy text={formatDate(createdAt)?.[0] || ''} />;
      },
      sorter: (a: BoxTableData, b: BoxTableData) =>
        a.createdAt.toString().localeCompare(b.createdAt.toString()),
      title: 'Created At'
    },
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (updatedAt: Date) => {
        return <Copy text={formatDate(updatedAt)?.[0] || ''} />;
      },
      sorter: (a: BoxTableData, b: BoxTableData) =>
        a.updatedAt.toString().localeCompare(b.updatedAt.toString()),
      title: 'Updated At'
    },
    {
      key: 'edit',
      render: (category: BoxTableData) => <Tag onClick={() => openDetail(category)}>Edit</Tag>
    }
  ];

  const onNotificationDelete = async () => {
    selectedRowKeys?.forEach((item: number) => {
      mutate(item.toString());
    });
  };

  const openDetail = (item: BoxTableData) => {
    history.push({
      pathname: `/box/${item._id}`
    });
  };

  /** Newly submitted, never opened survey results are shown in bold. */
  const rowClassName = (record: BoxTableData) =>
    isCurrentProjectCitizen && isSurveyResultBoxType(record.type) && !record.isRead
      ? classes.unreadRow
      : '';

  const actions = [];

  if (selectedRowKeys?.length) {
    actions.unshift({
      onClick: onNotificationDelete,
      title: 'Delete',
      type: 'primary'
    });
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: 'calc(100vh - 155px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={data?.paging?.total} title="Notifications" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={filteredData}
        loading={isLoading}
        rowClassName={rowClassName}
        searchBy={SearchBy.TITLE}
        // @ts-ignore
        selectedRowKeys={selectedRowKeys}
        // @ts-ignore
        setSelectedRowKeys={setSelectedRowKeys}
        totalPages={data?.paging?.total}
      />
    </div>
  );
};

export default Boxes;
